import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import type { PageTreeNode } from '../services/api';

const ChevronIcon = ({ open }: { open: boolean }) => (
  <svg
    viewBox="0 0 20 20"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    className={`h-3 w-3 shrink-0 transition-transform ${open ? 'rotate-90' : ''}`}
  >
    <path d="M7 5l6 5-6 5" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface PageLinkProps {
  appSlug: string;
  node: PageTreeNode;
  path: string[];
  isActive: boolean;
}

const PageLink = ({ appSlug, node, path, isActive }: PageLinkProps) => (
  <li>
    <Link
      to={`/${appSlug}/${path.join('/')}`}
      className={`block truncate rounded-md px-2.5 py-1.5 no-underline transition-colors ${
        isActive
          ? 'bg-brand-50 font-medium text-brand-700'
          : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'
      }`}
    >
      {node.title}
    </Link>
  </li>
);

interface CategoryItemProps {
  appSlug: string;
  node: PageTreeNode;
  path: string[];
  activePath: string[];
}

const CategoryItem = ({ appSlug, node, path, activePath }: CategoryItemProps) => {
  const isAncestor =
    activePath.length > path.length && activePath.slice(0, path.length).join('/') === path.join('/');
  const [open, setOpen] = useState(isAncestor);

  useEffect(() => {
    if (isAncestor) setOpen(true);
  }, [isAncestor]);

  return (
    <li>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex w-full items-center gap-1.5 rounded-md px-2 py-1.5 text-left text-xs font-semibold uppercase tracking-wide text-neutral-400 hover:text-neutral-600"
      >
        <ChevronIcon open={open} />
        {node.title}
      </button>
      {open && (
        <ul className="ml-2 space-y-0.5 border-l border-neutral-200 pl-2">
          {node.children.map((child) => (
            <TreeItem key={child.slug} appSlug={appSlug} node={child} parentPath={path} activePath={activePath} />
          ))}
        </ul>
      )}
    </li>
  );
};

interface TreeItemProps {
  appSlug: string;
  node: PageTreeNode;
  parentPath: string[];
  activePath: string[];
}

const TreeItem = ({ appSlug, node, parentPath, activePath }: TreeItemProps) => {
  const path = [...parentPath, node.slug];
  if (node.hasContent) {
    return <PageLink appSlug={appSlug} node={node} path={path} isActive={path.join('/') === activePath.join('/')} />;
  }
  return <CategoryItem appSlug={appSlug} node={node} path={path} activePath={activePath} />;
};

interface DocSidebarProps {
  appSlug: string;
  tree: PageTreeNode[];
  activePath: string[];
}

const DocSidebar = ({ appSlug, tree, activePath }: DocSidebarProps) => (
  <nav className="text-sm">
    <ul className="space-y-0.5">
      {tree.map((node) => (
        <TreeItem key={node.slug} appSlug={appSlug} node={node} parentPath={[]} activePath={activePath} />
      ))}
    </ul>
  </nav>
);

export default DocSidebar;
